import { useCallback } from 'react';
import {
  domainForSegment,
  getErrorTypeByCode,
  getAnalyticsBaseConfig,
  logoutErrorHandler,
} from 'common';
import {
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
} from 'common/utils/constants/AnalyticsConstants';

type AccountSetupData = {
  name: string;
  password: string;
  confirmPassword: string;
};

type AccountSetupValidationOptions = {
  formData: any;
  page: number;
  pathname: string;
  t: (key: string) => string;
  logging: any;
  formSubmissionFailed: (payload: any) => void;
  formSubmissionSucceeded: (payload: any) => void;
  registerUserName: (userName: string) => Promise<any>;
  registerUserNameValidation: (userName: string) => Promise<any>;
  validateUserPassword: (payload: any) => Promise<any>;
  getPasswordValidationPayload: () => any;
  setFormData: (data: any) => void;
  setLoader: (value: boolean) => void;
  setErrorMessage: (value: boolean) => void;
  setErrorMessage1: (value: boolean) => void;
  setErrorPassword: (value: boolean) => void;
  usernameErrorFormConfig: any;
  passwordErrorFormConfig: any;
  successFormConfig: any;
};

const USERNAME_VALID_ERROR_CODES = ['441', '400', '442', '445'];
const USERNAME_EXISTS_ERROR_CODES = ['449', '439', '409'];
const PASSWORD_ERROR_CODES = ['441', '400', '442', '445', '439', '443', '448', '422'];

const logValidationError = ({
  logging,
  response,
  page,
  userId = '',
}: {
  logging: any;
  response: any;
  page: number;
  userId?: string;
}) => {
  logging?.error({
    message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_EXCEPTION,
    screenName: SCREEN_NAMES.REGISTRATION,
    additionalDetails: {
      ...getAnalyticsBaseConfig({ userId }),
      step: page,
    },
    apiDetails: {
      exception: {
        errorType: getErrorTypeByCode(response?.errorCode?.toString()),
        message: response?.errorDetails?.detail,
        stack: response?.error,
      },
      response: { httpStatus: response?.errorCode },
    },
  });
};

export const useAccountSetupValidation = ({
  formData,
  page,
  pathname,
  t,
  logging,
  formSubmissionFailed,
  formSubmissionSucceeded,
  registerUserName,
  registerUserNameValidation,
  validateUserPassword,
  getPasswordValidationPayload,
  setFormData,
  setLoader,
  setErrorMessage,
  setErrorMessage1,
  setErrorPassword,
  usernameErrorFormConfig,
  passwordErrorFormConfig,
  successFormConfig,
}: AccountSetupValidationOptions) => {
  const handleUsernameError = useCallback((errorCode: string): void => {
    const isValidUser = USERNAME_VALID_ERROR_CODES.includes(errorCode.toString());
    const isUserExist = USERNAME_EXISTS_ERROR_CODES.includes(errorCode.toString());

    if (isValidUser) {
      setErrorMessage(true);
      setErrorMessage1(false);
    } else if (isUserExist) {
      setErrorMessage(false);
      setErrorMessage1(true);
    } else {
      setErrorMessage(false);
      setErrorMessage1(false);
    }

    setLoader(false);
  }, [setErrorMessage, setErrorMessage1, setLoader]);

  const handlePasswordError = useCallback((errorCode: string): void => {
    setErrorPassword(PASSWORD_ERROR_CODES.includes(errorCode.toString()));
  }, [setErrorPassword]);

  const userPasswordValidation = useCallback(async () => {
    const response = await validateUserPassword(getPasswordValidationPayload());

    if (logoutErrorHandler(response?.errorCode)) {
      return false;
    }

    if (response?.errorCode) {
      logValidationError({
        logging,
        response,
        page,
      });
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...passwordErrorFormConfig,
          formFailReason: response?.error ?? 'unknown',
        },
      });
      handlePasswordError(response?.errorCode);
      return false;
    }

    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig(),
      message: ANALYTICS_MESSAGES.REG_ACCOUNTSETUP_SUCCESS,
      screenName: SCREEN_NAMES.REGISTRATION,
    });
    formSubmissionSucceeded({
      segment: `${domainForSegment}${pathname}`,
      form: successFormConfig,
    });
    setErrorPassword(false);
    return true;
  }, [
    formSubmissionFailed,
    formSubmissionSucceeded,
    getPasswordValidationPayload,
    handlePasswordError,
    logging,
    page,
    passwordErrorFormConfig,
    pathname,
    setErrorPassword,
    successFormConfig,
    validateUserPassword,
  ]);

  const usernameValidation = useCallback(async (data: AccountSetupData) => {
    setFormData({ ...formData, [page]: { ...data } });
    const response = await registerUserName(data?.name);

    if (logoutErrorHandler(response?.errorCode)) {
      return false;
    }

    if (response?.errorCode) {
      logValidationError({
        logging,
        response,
        page,
      });
      handleUsernameError(response?.errorCode);
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...usernameErrorFormConfig,
          formFailReason: 'Requested Username already exists',
        },
      });
      return false;
    }

    setErrorMessage(false);
    setErrorMessage1(false);
    const registerUserResponse = await registerUserNameValidation(data.name);

    if (logoutErrorHandler(registerUserResponse?.errorCode)) {
      return false;
    }

    if (registerUserResponse?.errorCode) {
      logValidationError({
        logging,
        response: registerUserResponse,
        page,
      });
      handleUsernameError(registerUserResponse?.errorCode);
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...usernameErrorFormConfig,
          formFailReason: t('activation.userNameErrorMsg1'),
        },
      });
      return false;
    }

    return userPasswordValidation();
  }, [
    formData,
    formSubmissionFailed,
    handleUsernameError,
    logging,
    page,
    pathname,
    registerUserName,
    registerUserNameValidation,
    setErrorMessage,
    setErrorMessage1,
    setFormData,
    t,
    userPasswordValidation,
    usernameErrorFormConfig,
  ]);

  return {
    usernameValidation,
  };
};
